import React, { useState } from 'react'
import { Helmet } from 'react-helmet-async'
import * as FiIcons from 'react-icons/fi'
import SafeIcon from '../components/ui/SafeIcon'

const { FiSearch, FiMessageCircle, FiPhone, FiMail, FiChevronDown } = FiIcons

function HelpPage() {
  const [activeCategory, setActiveCategory] = useState(null)

  const faqCategories = [
    {
      title: 'Bestellung & Zahlung',
      questions: [
        {
          question: 'Wie bestelle ich Produkte auf 1688Euro.com?',
          answer: 'Wählen Sie Produkte aus, legen Sie sie in den Warenkorb und folgen Sie dem Checkout-Prozess. Sie können auch direkt mit Lieferanten chatten.'
        },
        {
          question: 'Welche Zahlungsmethoden werden auf 1688Euro.com akzeptiert?',
          answer: 'Wir akzeptieren Banküberweisung, Kreditkarte, PayPal und andere sichere Zahlungsmethoden.'
        },
        {
          question: 'Gibt es Mindestbestellmengen bei 1688Euro.com?',
          answer: 'Ja, viele Produkte haben Mindestbestellmengen (MOQ), die je nach Lieferant variieren.'
        }
      ]
    },
    {
      title: 'Versand & Lieferung',
      questions: [
        {
          question: 'Wie lange dauert die Lieferung bei 1688Euro.com?',
          answer: 'Standardversand: 15-30 Tage, Express: 5-10 Tage. Die genaue Lieferzeit hängt vom Lieferanten ab.'
        },
        {
          question: 'Welche Versandoptionen gibt es bei 1688Euro.com?',
          answer: 'Luftfracht, Seefracht, Express (DHL, FedEx) und kombinierte Logistik-Lösungen.'
        },
        {
          question: 'Kosten für den Versand bei 1688Euro.com?',
          answer: 'Die Versandkosten werden basierend auf Gewicht, Volumen und Zielort berechnet.'
        }
      ]
    },
    {
      title: 'Zoll & Import',
      questions: [
        {
          question: 'Wer kümmert sich um die Zollabwicklung bei 1688Euro.com?',
          answer: 'Wir bieten komplette Zollabwicklungsdienste an. Unsere Experten kümmern sich um alle Formalitäten.'
        },
        {
          question: 'Welche Dokumente werden für den Import benötigt?',
          answer: 'Handelsrechnung, Packliste, Ursprungszeugnis und ggf. spezielle Zertifikate je nach Produktart.'
        },
        {
          question: 'Welche Einfuhrsteuern fallen bei 1688Euro.com an?',
          answer: 'Dies hängt vom Produktwert und dem Bestimmungsland ab. Wir berechnen diese Kosten transparent vorab.'
        }
      ]
    },
    {
      title: 'Kontakt & Support',
      questions: [
        {
          question: 'Wie kann ich den Support von 1688Euro.com kontaktieren?',
          answer: 'Per Live-Chat, E-Mail (support@1688euro.com) oder Telefon +49 30 12345678'
        },
        {
          question: 'Support-Zeiten bei 1688Euro.com?',
          answer: 'Montag bis Freitag: 9:00-18:00 Uhr, Samstag: 10:00-14:00 Uhr (MEZ)'
        },
        {
          question: 'Sprachen des Supports bei 1688Euro.com?',
          answer: 'Deutsch, Englisch, Chinesisch. Unser Team spricht Ihre Sprache!'
        }
      ]
    }
  ]

  const contactMethods = [
    { icon: FiMessageCircle, title: 'Live Chat', description: 'Sofortige Hilfe von unserem Support-Team', action: 'Chat starten', color: 'text-green-600', bgColor: 'bg-green-100' },
    { icon: FiPhone, title: 'Telefon', description: '+49 30 12345678', action: 'Anrufen', color: 'text-blue-600', bgColor: 'bg-blue-100' },
    { icon: FiMail, title: 'E-Mail', description: 'support@1688euro.com', action: 'E-Mail senden', color: 'text-orange-600', bgColor: 'bg-orange-100' }
  ]

  return (
    <>
      <Helmet>
        <title>Hilfe & Support - Häufige Fragen | 1688Euro.com</title>
        <meta name="description" content="Hilfe & Support bei 1688Euro.com - Antworten auf häufige Fragen zu Bestellung, Versand, Zoll und Support. Kontaktieren Sie uns bei weiteren Fragen." />
        <meta name="keywords" content="Hilfe,Support,FAQ,Kontakt,1688Euro.com,Beratung,Import China" />
        <link rel="canonical" href="https://1688euro.com/help" />
        
        {/* FAQ Schema Markup */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": faqCategories.flatMap(category => 
              category.questions.map(qa => ({
                "@type": "Question",
                "name": qa.question,
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": qa.answer
                }
              }))
            )
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-[#F2F3F7] py-8">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 mb-8 text-center">
            <h1 className="text-3xl font-bold text-gray-900 mb-4">Hilfe & Support - 1688Euro.com</h1>
            <div className="max-w-2xl mx-auto relative">
              <input type="text" placeholder="Suchen Sie nach Antworten..." className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-[#FF6600]" />
              <SafeIcon icon={FiSearch} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            </div>
          </div>

          {/* Contact Methods */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            {contactMethods.map((method, index) => (
              <div key={index} className="bg-white rounded-lg border border-gray-200 p-6 text-center hover:shadow-lg transition-shadow">
                <div className={`w-12 h-12 ${method.bgColor} rounded-full flex items-center justify-center mx-auto mb-4`}>
                  <SafeIcon icon={method.icon} className={`w-6 h-6 ${method.color}`} />
                </div>
                <h3 className="font-bold text-gray-900 mb-2">{method.title}</h3>
                <p className="text-sm text-gray-600 mb-4">{method.description}</p>
                <button className="text-[#FF6600] font-medium hover:underline">{method.action}</button>
              </div>
            ))}
          </div>

          {/* FAQ Section */}
          <div className="bg-white rounded-lg border border-gray-200 p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Häufig gestellte Fragen (FAQ) - 1688Euro.com</h2>
            {faqCategories.map((category, categoryIndex) => (
              <div key={categoryIndex} className="mb-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4">{category.title}</h3>
                <div className="space-y-3">
                  {category.questions.map((faq, faqIndex) => (
                    <div key={faqIndex} className="border border-gray-200 rounded-lg">
                      <button className="w-full flex justify-between items-center p-4 text-left hover:bg-gray-50" onClick={() => setActiveCategory(activeCategory === `${categoryIndex}-${faqIndex}` ? null : `${categoryIndex}-${faqIndex}`)}>
                        <span className="font-medium text-gray-900">{faq.question}</span>
                        <SafeIcon icon={FiChevronDown} className={`w-5 h-5 text-gray-400 transition-transform ${activeCategory === `${categoryIndex}-${faqIndex}` ? 'rotate-180' : ''}`} />
                      </button>
                      {activeCategory === `${categoryIndex}-${faqIndex}` && (
                        <div className="p-4 bg-gray-50 border-t border-gray-200">
                          <p className="text-gray-600">{faq.answer}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Emergency Contact */}
          <div className="bg-gradient-to-r from-red-600 to-red-500 rounded-lg p-6 text-white text-center mt-8">
            <h3 className="text-xl font-bold mb-2">Dringende Hilfe bei 1688Euro.com benötigt?</h3>
            <p className="mb-4">Unser Notfall-Support ist 24/7 für dringende Lieferprobleme erreichbar</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button className="bg-white text-red-600 px-6 py-2 rounded-lg font-bold hover:bg-gray-100"> Notfall-Kontakt </button>
              <button className="border border-white text-white px-6 py-2 rounded-lg font-bold hover:bg-white hover:text-red-600"> Live-Chat starten </button>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default HelpPage